import React, { FC, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { ProductSize } from '@/network/graphql.g'
import { validFor } from '@/theme/validFor'
import { shortenValue } from '@/components/molecules/productSizeBox'
import { ProductCardMode } from '@/components/organisms/productCard/productCardMode'
import {
  ExperimentType,
  ProductListingSizesVariant,
  useVariant
} from '@/providers/experimentProvider'

const MaxTiles = 5
const TileGap = 4

// Sizes always visible under the image in product listings on mobile and tablet
// (ProductListingSizes experiment); desktop keeps the sizes in the hover menu
export const useShowListingSizes = (mode: ProductCardMode): boolean =>
  useVariant(ExperimentType.ProductListingSizes) ===
    ProductListingSizesVariant.Sizes && mode === ProductCardMode.productList

// Number of equally wide tiles in the row: 5 when every shown size fits into a tile, fewer
// (and wider) tiles for long sizes. With more sizes than tiles the last tile is "+N".
const countTiles = (
  sizeWidths: number[],
  plusWidth: number,
  rowWidth: number
): number => {
  const minTiles = sizeWidths.length === 1 ? 1 : 2
  for (let tiles = MaxTiles; tiles > minTiles; tiles--) {
    const tileWidth = (rowWidth - (tiles - 1) * TileGap) / tiles
    const shownWidths =
      sizeWidths.length <= tiles
        ? sizeWidths
        : [...sizeWidths.slice(0, tiles - 1), plusWidth]
    if (Math.max(...shownWidths) <= tileWidth) {
      return tiles
    }
  }
  return minTiles
}

type ProductCardSizesProps = {
  sizes?: ProductSize[]
}

const ProductCardSizes: FC<ProductCardSizesProps> = ({ sizes }) => {
  const rowRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [tileCount, setTileCount] = useState<number>(MaxTiles)

  const values = sizes?.map(({ value }) => shortenValue(value)) ?? []
  const valuesKey = values.join('|')

  useEffect(() => {
    const row = rowRef.current
    const measure = measureRef.current
    if (!row || !measure || !valuesKey) {
      return
    }
    const update = () => {
      const widthOf = (element: Element) =>
        element.getBoundingClientRect().width
      const tiles = Array.from(measure.children)
      const plusTile = tiles.pop()
      setTileCount(
        countTiles(tiles.map(widthOf), widthOf(plusTile), widthOf(row))
      )
    }
    update()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(update)
    observer.observe(row)
    return () => observer.disconnect()
  }, [valuesKey])

  const hasPlus = values.length > tileCount
  const shownValues = hasPlus ? values.slice(0, tileCount - 1) : values

  return (
    <Row ref={rowRef} $tiles={tileCount} data-testid="productCardSizes">
      {shownValues.map((value, index) => (
        <SizeTile key={index}>{value}</SizeTile>
      ))}
      {hasPlus && <PlusTile>+{values.length - shownValues.length}</PlusTile>}
      <MeasureRow ref={measureRef} aria-hidden={true}>
        {values.map((value, index) => (
          <SizeTile key={index}>{value}</SizeTile>
        ))}
        <PlusTile>+{values.length}</PlusTile>
      </MeasureRow>
    </Row>
  )
}

export default ProductCardSizes

const Row = styled.div<{ $tiles: number }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${({ $tiles }) => $tiles}, minmax(0, 1fr));
  gap: ${TileGap}px;
  height: 24px;
  margin-top: 8px;
  overflow: hidden;
`

const MeasureRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  gap: ${TileGap}px;
  width: max-content;
  visibility: hidden;
  pointer-events: none;
`

const Tile = css`
  height: 24px;
  padding: 0 3px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${validFor.mobile`
    font-size: 11px;
  `}
`

const SizeTile = styled.span`
  ${Tile};
  background-color: #f3f3f3;
  color: ${({ theme }) => theme.colors.onBackground};
`

const PlusTile = styled.span`
  ${Tile};
  background-color: ${({ theme }) => theme.colors.onBackground};
  color: ${({ theme }) => theme.colors.background};
`
